import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/auth-store';
import { LoginPage } from '@/pages/login';
import { AppLayout } from '@/components/layouts/app-layout';
import Dashboard from '@/pages/dashboard';
import { ClientsPage } from '@/pages/clients';
import { ClientFormPage } from '@/pages/clients/form';
import { TagsPage } from '@/pages/tags';
import { CompaniesPage } from '@/pages/companies';
import { CompanyFormPage } from '@/pages/companies/form';
import { LoadingScreen } from '@/components/loading-screen';
import { WhatsAppIndex } from '@/pages/whatsapp';
import { WhatsAppConfig } from '@/pages/whatsapp/config';
import { WhatsAppTest } from '@/pages/whatsapp/test';
import { WhatsAppConnections } from '@/pages/whatsapp/connections';
import { EmailTemplatesPage } from '@/pages/email-templates';
import { EmailTemplateFormPage } from '@/pages/email-templates/form';
import { SettingsPage } from '@/pages/settings';
import { IntegrationsPage } from '@/pages/settings/integrations';
import { BrandingPage } from '@/pages/settings/branding';
import { ListsPage } from '@/pages/lists';
import { ListFormPage } from '@/pages/lists/form';
import { EventsPage } from '@/pages/events';
import { EventWizard } from '@/pages/events/wizard';
import { EventGuestsPage } from '@/pages/events/guests';
import { FlowsPage } from '@/pages/flows';
import { FlowEditor } from '@/pages/flows/editor';
import { ExecuteFlowPage } from '@/pages/flows/execute';
import { FlowExecutions } from '@/pages/flows/executions';
import { ExecutionDetails } from '@/pages/flows/execution-details';
import { FlowExecutionsList } from '@/pages/flows/flow-executions-list';
import { FormSubmissionsPage } from '@/pages/flows/form-submissions';
import { Products } from '@/pages/products';
import { ProductForm } from '@/pages/products/form';
import { ProductCategories } from '@/pages/products/categories';
import { ClientCategories } from '@/pages/client-categories';
import { OrdersPage } from '@/pages/orders';
import { StorePage } from '@/pages/store';
import { StoreProductPage } from '@/pages/store/product';
import { StoreCartPage } from '@/pages/store/cart';
import { StoreCheckoutPage } from '@/pages/store/checkout';
import { OrderConfirmationPage } from '@/pages/store/order-confirmation';
import { QuickSalePage } from '@/pages/quick-sale';
import { ClientRegister } from '@/pages/client/register';
import { ClientLogin } from '@/pages/client/login';
import { ClientForgotPassword } from '@/pages/client/forgot-password';
import { ClientResetPassword } from '@/pages/client/reset-password';
import { ClientDashboard } from '@/pages/client/dashboard';
import CheckInPage from '@/pages/internal-orders/checkin';
import TablesPage from '@/pages/internal-orders/tables';
import ComandasPage from '@/pages/internal-orders/comandas';
import PublicFormPage from '@/pages/form';
import { PublicEventPage } from '@/pages/public-event';
import { ConfirmEventPage } from '@/pages/public-event/confirm';
import { DeclineEventPage } from '@/pages/public-event/decline';
import { ProcessesPage } from '@/pages/processes';
import { ProcessFormPage } from '@/pages/processes/form';
import { PositionsPage } from '@/pages/positions';
import { PositionFormPage } from '@/pages/positions/form';
import { EmployeesPage } from '@/pages/employees';
import { EmployeeProcesses } from '@/pages/employees/processes';
import { MyProcessesPage } from '@/pages/my-processes';
import { ActivitiesDashboard } from '@/pages/dashboard/activities';
import { VenuesPage } from '@/pages/venues';
import { VenueFormPage } from '@/pages/venues/form';
import { VenueReservationsPage } from '@/pages/venue-reservations';
import { VenueReservationFormPage } from '@/pages/venue-reservations/form';
import { VenueReservationsCalendarPage } from '@/pages/venue-reservations/calendar';
import { VenueReservationDetailsPage } from '@/pages/venue-reservations/details';
import { PublicVenuesPage } from '@/pages/public-venues';
import { PublicVenueDetailsPage } from '@/pages/public-venues/details';
import { ReservationCheckoutPage } from '@/pages/venue-reservations/checkout';
import { CalendarPage } from '@/pages/calendar';
import { UsersPage } from '@/pages/users';
import { UserFormPage } from '@/pages/users/form';
import { ProfilePage } from '@/pages/profile';
import { UserGroupsPage } from '@/pages/user-groups';
import { UserGroupFormPage } from '@/pages/user-groups/form';
import { BoardsPage } from '@/pages/boards';
import { BoardFormPage } from '@/pages/boards/form';
import { BoardKanbanPage } from '@/pages/boards/kanban';
import { WebChatPage } from '@/pages/webchat';
import { DepartmentsPage } from '@/pages/departments';
import { DepartmentUsersPage } from '@/pages/departments/users';
import { useEffect, useState } from 'react';

function PrivateRoute({ children }: { children: React.ReactNode }) {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Give time for the store to hydrate
    const timer = setTimeout(() => setIsLoading(false), 100);
    return () => clearTimeout(timer);
  }, []);

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

export function Router() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />

      {/* Quick Sale Route - Public (legacy path) */}
      <Route path="/venda-rapida/:clientId/:productId" element={<QuickSalePage />} />

      {/* Public Store Routes */}
      <Route path="/loja" element={<StorePage />} />
      <Route path="/loja/produto/:id" element={<StoreProductPage />} />
      <Route path="/loja/carrinho" element={<StoreCartPage />} />
      <Route path="/loja/checkout" element={<StoreCheckoutPage />} />
      <Route path="/loja/pedido/:orderNumber" element={<OrderConfirmationPage />} />
      <Route path="/loja/venda-rapida/:clientId/:productId" element={<QuickSalePage />} />

      {/* Client Area Routes */}
      <Route path="/client/register" element={<ClientRegister />} />
      <Route path="/client/login" element={<ClientLogin />} />
      <Route path="/client/forgot-password" element={<ClientForgotPassword />} />
      <Route path="/client/reset-password/:token" element={<ClientResetPassword />} />
      <Route path="/client/dashboard" element={<ClientDashboard />} />

      {/* Public Event Routes */}
      <Route path="/evento/:slug" element={<PublicEventPage />} />
      <Route path="/evento/:slug/confirmar/:token" element={<ConfirmEventPage />} />
      <Route path="/evento/:slug/recusar/:token" element={<DeclineEventPage />} />

      {/* Public Venues Routes */}
      <Route path="/ambientes" element={<PublicVenuesPage />} />
      <Route path="/ambientes/:id" element={<PublicVenueDetailsPage />} />

      {/* Public Reservation Checkout */}
      <Route path="/reserva/checkout/:id" element={<ReservationCheckoutPage />} />

      {/* Public Form Route */}
      <Route path="/form/:token" element={<PublicFormPage />} />

      {/* Internal Orders - Fullscreen Check-in */}
      <Route
        path="/checkin"
        element={
          <PrivateRoute>
            <CheckInPage />
          </PrivateRoute>
        }
      />

      <Route
        path="/app"
        element={
          <PrivateRoute>
            <AppLayout />
          </PrivateRoute>
        }
      >
        <Route index element={<Navigate to="/app/dashboard" replace />} />
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="dashboard/atividades" element={<ActivitiesDashboard />} />
        <Route path="meus-processos" element={<MyProcessesPage />} />
        <Route path="clientes" element={<ClientsPage />} />
        <Route path="clientes/novo" element={<ClientFormPage />} />
        <Route path="clientes/:id" element={<ClientFormPage />} />
        <Route path="categorias-clientes" element={<ClientCategories />} />
        <Route path="tags" element={<TagsPage />} />
        <Route path="empresas" element={<CompaniesPage />} />
        <Route path="empresas/nova" element={<CompanyFormPage />} />
        <Route path="empresas/:id" element={<CompanyFormPage />} />
        <Route path="whatsapp" element={<WhatsAppIndex />} />
        <Route path="whatsapp/connections" element={<WhatsAppConnections />} />
        <Route path="whatsapp/config" element={<WhatsAppConfig />} />
        <Route path="whatsapp/test" element={<WhatsAppTest />} />
        <Route path="email-templates" element={<EmailTemplatesPage />} />
        <Route path="email-templates/:id" element={<EmailTemplateFormPage />} />
        <Route path="listas" element={<ListsPage />} />
        <Route path="listas/nova" element={<ListFormPage />} />
        <Route path="listas/:id" element={<ListFormPage />} />
        <Route path="eventos" element={<EventsPage />} />
        <Route path="eventos/novo" element={<EventWizard />} />
        <Route path="eventos/:id/editar" element={<EventWizard />} />
        <Route path="eventos/:id/convidados" element={<EventGuestsPage />} />
        <Route path="fluxos" element={<FlowsPage />} />
        <Route path="fluxos/execucoes" element={<FlowExecutions />} />
        <Route path="fluxos/execucoes/:executionId" element={<ExecutionDetails />} />
        <Route path="fluxos/:flowId/execucoes-lista" element={<FlowExecutionsList />} />
        <Route path="fluxos/formularios" element={<FormSubmissionsPage />} />
        <Route path="fluxos/novo" element={<FlowEditor />} />
        <Route path="fluxos/:id/editar" element={<FlowEditor />} />
        <Route path="fluxos/:id/executar" element={<ExecuteFlowPage />} />
        <Route path="produtos">
          <Route index element={<Products />} />
          <Route path="novo" element={<ProductForm />} />
          <Route path="categorias" element={<ProductCategories />} />
          <Route path=":id" element={<ProductForm />} />
        </Route>
        <Route path="pedidos" element={<OrdersPage />} />
        <Route path="mesas" element={<TablesPage />} />
        <Route path="comandas" element={<ComandasPage />} />
        <Route path="funcionarios" element={<EmployeesPage />} />
        <Route path="agenda" element={<CalendarPage />} />
        <Route path="funcionarios/:id/processos" element={<EmployeeProcesses />} />
        <Route path="processos" element={<ProcessesPage />} />
        <Route path="processos/novo" element={<ProcessFormPage />} />
        <Route path="processos/editar/:id" element={<ProcessFormPage />} />
        <Route path="cargos" element={<PositionsPage />} />
        <Route path="cargos/novo" element={<PositionFormPage />} />
        <Route path="cargos/:id" element={<PositionFormPage />} />
        <Route path="venues" element={<VenuesPage />} />
        <Route path="venues/new" element={<VenueFormPage />} />
        <Route path="venues/:id" element={<VenueFormPage />} />
        <Route path="venue-reservations" element={<VenueReservationsPage />} />
        <Route path="venue-reservations/new" element={<VenueReservationFormPage />} />
        <Route path="venue-reservations/calendar" element={<VenueReservationsCalendarPage />} />
        <Route path="venue-reservations/:id" element={<VenueReservationDetailsPage />} />
        <Route path="venue-reservations/:id/edit" element={<VenueReservationFormPage />} />
        <Route path="usuarios" element={<UsersPage />} />
        <Route path="usuarios/novo" element={<UserFormPage />} />
        <Route path="usuarios/:id" element={<UserFormPage />} />
        <Route path="grupos" element={<UserGroupsPage />} />
        <Route path="grupos/novo" element={<UserGroupFormPage />} />
        <Route path="grupos/:id" element={<UserGroupFormPage />} />
        <Route path="perfil" element={<ProfilePage />} />
        <Route path="settings" element={<SettingsPage />} />
        <Route path="settings/integracoes" element={<IntegrationsPage />} />
        <Route path="settings/cores" element={<BrandingPage />} />
        <Route path="quadros" element={<BoardsPage />} />
        <Route path="quadros/novo" element={<BoardFormPage />} />
        <Route path="quadros/:id" element={<BoardKanbanPage />} />
        <Route path="quadros/:id/editar" element={<BoardFormPage />} />
        <Route path="webchat" element={<WebChatPage />} />
        <Route path="departamentos" element={<DepartmentsPage />} />
        <Route path="departamentos/:id/usuarios" element={<DepartmentUsersPage />} />
      </Route>

      <Route path="/" element={<Navigate to="/login" replace />} />
    </Routes>
  );
}